import StatusInterface from '@/components/common/StatusInterface'
import { CalenderIcon, LeafIcon } from '@/components/icons'
import PackVariantsIcon from '@/components/icons/PackVariantsIcon'
import SettingTwoIcon from '@/components/icons/SettingTwoIcon'
import WaterLevelSoilDarkIcon from '@/components/icons/WaterLevelSoilDarkIcon'
import useSearchInterfaceId from '@/features/admin/hooks/interface/useSearchInterfaceId'
import { useAuthContext } from '@/features/auth/context'
import WaterSource from '@/features/config/pages/WaterSource'
import useTranslation from '@/hooks/useTranslation'
import { selectTheme } from '@/store/selectors/ThemeSelector'
import { mode_type } from '@/theme/types/constants'
import { TabContext, TabPanel } from '@mui/lab'
import { Box, Stack, Tab, Tabs } from '@mui/material'
import { lowerCase, snakeCase } from 'lodash'
import { FC, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { ConfigLayout } from '../../components/common'
import ConfigHeader from '../../components/common/Header'
import EtReference from '../EtReference'
// SCRUM-1011: Fertigation tab hidden, keep import for re-enable
// import Fertigation from '../Fertigation'
import GlobalConfigurations from '../GlobalConfigurations'
import Hydraulic from '../Hydraulic'
import { DataConfigTabProvider, E_DataConfigTab } from './DataConfigTabContext'
import PlantSoilEmitter from './PlantSoilEmitter'
import classes from './styles.module.scss'

interface CustomTabProps {
  value: E_DataConfigTab
  icon: FC<{ sx: { marginRight: string; color: string } }>
  text: string
  color?: string
}

const DataConfiguration = () => {
  const { t } = useTranslation()
  const themeMode = useSelector(selectTheme)
  const { interfaceId, statusInterfaceId } = useAuthContext()
  const { data: statusInterface } = useSearchInterfaceId(interfaceId + '')
  const [tab, setTab] = useState<E_DataConfigTab>(E_DataConfigTab.Hydraulic)

  const handleChangeTab = (_: any, newTab: E_DataConfigTab) => {
    setTab(newTab)
  }

  const [title, setTitle] = useState('Hydraulic')

  const generateTabLabel = ({ value, icon: Icon, text }: CustomTabProps) => (
    <Box className={classes.boxTab}>
      <Icon
        sx={{
          marginRight: '5px',
          color: themeMode === mode_type.dark ? '#fff' : '#004744',
        }}
      />
      {text}
    </Box>
  )

  useEffect(() => {
    const tabConfig = DataConfigTabMapper[tab as keyof typeof DataConfigTabMapper]
    if (tabConfig) setTitle(tabConfig.title)
  }, [tab])
  const DataConfigTabMapper = {
    [E_DataConfigTab.Hydraulic]: {
      title: t('commonWordHydraulic'),
      value: '0',
      component: Hydraulic,
    },
    // SCRUM-1011: Fertigation tab hidden
    // [E_DataConfigTab.Fertigation]: {
    //   title: t('operationsOperationsModalFertigation'),
    //   value: '1',
    //   component: Fertigation,
    // },
    [E_DataConfigTab.ETReference]: {
      title: t('menuDataConfigEtReference'),
      value: '2',
      component: EtReference,
    },
    [E_DataConfigTab.PlantSoilEmitter]: {
      title: t('dataConfigTabStationConfigs'),
      value: '3',
      component: PlantSoilEmitter,
    },
    [E_DataConfigTab.GlobalConfigurations]: {
      title: t('commonWordGlobalConfiguration'),
      value: '4',
      component: GlobalConfigurations,
    },
    [E_DataConfigTab.WaterSources]: {
      title: t('waterSources'),
      value: '5',
      component: WaterSource,
    },
  }
  return (
    <DataConfigTabProvider currentTab={tab}>
    <ConfigLayout
      title="Data Configuration"
      header={<ConfigHeader subtitle={t('dataConfig')} title={title} />}
      fullWidth
    >
      <Stack
        className={`${classes.wrapper} ${
          statusInterface &&
          statusInterface.state === 'Offline' &&
          statusInterfaceId &&
          'wrapper-disabled'
        }`}
      >
        <Stack
          className={classes.groupLayoutHeader}
          direction="row"
          spacing={2}
        >
          <TabContext value={tab.toString()}>
            <Tabs
              value={tab}
              onChange={handleChangeTab}
              textColor="inherit"
              sx={{
                minHeight: 'unset',
                background: 'transparent',
                width: '100%',
                '.MuiTab-root': {
                  p: '0 6px',
                  flex: 1,
                  // Grows rather than clips: at a narrow window or high zoom
                  // the longest label ("Global Configurations") no longer fits
                  // one line, and wrapping reads better than an ellipsis.
                  height: 'auto',
                  minHeight: '36px',
                  fontWeight: 600,
                  lineHeight: '18px',
                  fontSize: '14px',
                  color: 'text.main',
                  // flex: 1 above shares the bar equally; 'fit-content' used
                  // to cap each tab at its label and undo that.
                  maxWidth: 'none',
                  whiteSpace: 'normal',
                  '&.Mui-selected': {
                    color: 'text.main',
                    borderBottom: '2px solid #004744',
                  },
                },
                '.MuiTabs-indicator': {
                  display: 'none',
                },
              }}
            >
              <Tab
                value={E_DataConfigTab.Hydraulic}
                label={generateTabLabel({
                  value: E_DataConfigTab.Hydraulic,
                  icon: WaterLevelSoilDarkIcon,
                  text: t('commonWordHydraulic'),
                })}
              />
              {/* SCRUM-1011: Fertigation tab hidden
              <Tab
                value={E_DataConfigTab.Fertigation}
                label={generateTabLabel({
                  value: E_DataConfigTab.Fertigation,
                  icon: PackVariantsIcon,
                  text: t('operationsOperationsModalFertigation'),
                })}
              /> */}
              <Tab
                value={E_DataConfigTab.ETReference}
                label={generateTabLabel({
                  value: E_DataConfigTab.ETReference,
                  icon: CalenderIcon,
                  text: t('menuDataConfigEtReference'),
                })}
              />
              <Tab
                value={E_DataConfigTab.PlantSoilEmitter}
                label={generateTabLabel({
                  value: E_DataConfigTab.PlantSoilEmitter,
                  icon: LeafIcon,
                  text: t('dataConfigTabStationConfigs'),
                })}
              />
              <Tab
                value={E_DataConfigTab.GlobalConfigurations}
                label={generateTabLabel({
                  value: E_DataConfigTab.GlobalConfigurations,
                  icon: SettingTwoIcon,
                  text: t('dataConfigLabelGlobalConfigurations'),
                })}
              />
              <Tab
                value={E_DataConfigTab.WaterSources}
                label={generateTabLabel({
                  value: E_DataConfigTab.WaterSources,
                  icon: WaterLevelSoilDarkIcon,
                  text: t('waterSources'),
                })}
              />
            </Tabs>
          </TabContext>
        </Stack>

        <TabContext value={tab.toString()}>
          <Stack flexGrow={1} height="100%">
            {Object.entries(DataConfigTabMapper).map(([key, tabData]) => {
              const ActiveComponent = tabData.component
              return (
                <TabPanel
                  key={snakeCase(lowerCase(tabData.title))}
                  value={tabData.value.toString()}
                  className={classes.tab}
                >
                  <ActiveComponent />
                </TabPanel>
              )
            })}
          </Stack>
        </TabContext>
      </Stack>
      <StatusInterface />
    </ConfigLayout>
    </DataConfigTabProvider>
  )
}

export default DataConfiguration
